import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // ── Startup ────────────────────────────────────────────────────────────────
  // The access token lives 15 minutes; the refresh cookie lives 7 days. So on
  // opening the app the stored token has almost always expired, and the only
  // correct response to that is to spend the refresh cookie — not to throw the
  // session away.
  //
  // This used to call /api/auth/me and, on the 401, clear localStorage. Which
  // meant everyone was logged out on every visit more than fifteen minutes
  // after their last one, despite holding a perfectly good week-long cookie.
  useEffect(() => {
    bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** GET /me with a token. null = the token is no good; throw = network. */
  const loadMe = async (accessToken) => {
    const response = await fetch('/api/auth/me', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) return null;
    const data = await response.json();
    return data.user;
  };

  const bootstrap = async () => {
    try {
      const savedToken = localStorage.getItem('accessToken');

      if (savedToken) {
        const me = await loadMe(savedToken);
        if (me) {
          setUser(me);
          setToken(savedToken);
          return;
        }
      }

      // Either there was no stored token or it had expired. The refresh cookie
      // is httpOnly, so we cannot inspect it — the only way to know whether a
      // session is still alive is to ask. `credentials: 'include'` is what
      // sends it. This is also what makes opening the app in a fresh tab, or
      // after clearing localStorage, resume the session rather than bounce to
      // the login page.
      const refreshed = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
      });
      if (!refreshed.ok) {
        clearSession();
        return;
      }
      const { accessToken } = await refreshed.json();
      const me = await loadMe(accessToken);
      if (!me) { clearSession(); return; }

      localStorage.setItem('accessToken', accessToken);
      setToken(accessToken);
      setUser(me);
    } catch (error) {
      // A network blip on startup is not a logout. Keeping the stored token
      // lets the next attempt succeed instead of forcing a fresh sign-in
      // because the wifi dropped for a second.
      console.error('Auth bootstrap failed:', error);
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const clearSession = () => {
    localStorage.removeItem('accessToken');
    setToken(null);
    setUser(null);
  };

  const login = async (email, password) => {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Login failed');
      }

      const data = await response.json();
      setToken(data.accessToken);
      setUser(data.user);
      localStorage.setItem('accessToken', data.accessToken);
      // The user comes back with the result so the caller can route on their
      // role immediately — reading it from context instead would be a render
      // behind, and the login page would navigate before the role was known.
      return { success: true, user: data.user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const setAuthState = (accessToken, userData) => {
    setToken(accessToken);
    setUser(userData);
    localStorage.setItem('accessToken', accessToken);
  };

  const refreshAccessToken = async () => {
    try {
      const r = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' });
      if (!r.ok) throw new Error('Refresh failed');
      const { accessToken } = await r.json();
      setToken(accessToken);
      localStorage.setItem('accessToken', accessToken);
      return accessToken;
    } catch {
      // Deliberately does NOT clear the session. This runs on a timer and on
      // every tab focus, so a single failed request — a dropped connection, a
      // server restart mid-deploy — must not sign everybody out. The token we
      // already hold stays valid until it expires, and the next tick retries.
      return null;
    }
  };

  // ── Keeping a live session alive ───────────────────────────────────────────
  // Fixing startup is only half of it: with a 15-minute token and nothing
  // renewing it, a tab left open goes stale and the next request 401s mid-use.
  //
  // Two triggers, because they catch different things. The interval covers a
  // tab someone is actively working in; the visibility handler covers the far
  // commoner case of a tab left in the background for an hour and then
  // returned to, where the interval may have been throttled to nothing by the
  // browser.
  useEffect(() => {
    if (!token) return undefined;

    const REFRESH_EVERY = 12 * 60 * 1000; // comfortably inside the 15m lifetime

    const renew = () => { refreshAccessToken().catch(() => {}); };
    const onVisible = () => { if (document.visibilityState === 'visible') renew(); };

    const handle = setInterval(renew, REFRESH_EVERY);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(handle);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const logout = async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('accessToken');
      setToken(null);
      setUser(null);
    }
  };

  // A preference the user just changed about themselves — their chat colour so
  // far. Patched into the context rather than refetched, so the change shows in
  // the header and the preview the moment it is saved.
  const applyUserChange = (patch) => setUser((prev) => (prev ? { ...prev, ...patch } : prev));

  /**
   * Look at the product as another role.
   *
   * The server mints a short-lived token that keeps your own user id and swaps
   * only the role and its permissions, so what you see is your own data as that
   * role would see it. The admin token is kept aside rather than thrown away:
   * exiting restores it immediately instead of waiting on a refresh.
   */
  const startViewAs = async (role) => {
    const r = await fetch('/api/auth/view-as', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ role }),
    });
    const body = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(body.error || 'Could not start the preview');

    sessionStorage.setItem('adminToken', token);
    setToken(body.accessToken);
    localStorage.setItem('accessToken', body.accessToken);
    setUser((prev) => (prev ? {
      ...prev,
      role: body.role,
      permissions: body.permissions,
      viewAs: { realRole: prev.role, realUserId: prev.id },
    } : prev));
    return body.role;
  };

  const stopViewAs = async () => {
    // Ask the server for a fresh token built from the real role in the
    // database, rather than trusting the one we stashed — exiting a preview
    // must never be a way to widen permissions.
    const r = await fetch('/api/auth/view-as/exit', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    const body = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(body.error || 'Could not end the preview');

    sessionStorage.removeItem('adminToken');
    setToken(body.accessToken);
    localStorage.setItem('accessToken', body.accessToken);
    setUser((prev) => (prev ? {
      ...prev, role: body.role, permissions: body.permissions, viewAs: null,
    } : prev));
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      isAuthenticated: !!token && !!user,
      loading,
      login,
      logout,
      setAuthState,
      refreshAccessToken,
      applyUserChange,
      startViewAs,
      stopViewAs,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
